import React, { useState, useEffect, useRef } from 'react';
import { format } from 'date-fns';
import driverMessageService, { DriverMessage } from '../../services/driverMessageService';
import { toast } from 'react-hot-toast';
import { IoSend, IoImage, IoClose } from 'react-icons/io5';
import { imageUploadService } from '../../services';

interface DriverMessageChatProps {
  deliveryId: string;
  driverId?: string | null;
  driverName?: string;
}

const DriverMessageChat: React.FC<DriverMessageChatProps> = ({ 
  deliveryId, 
  driverId,
  driverName = 'Driver'
}) => {
  const [messages, setMessages] = useState<DriverMessage[]>([]);
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [messageText, setMessageText] = useState('');
  const [images, setImages] = useState<string[]>([]);
  const [previewImages, setPreviewImages] = useState<{ url: string, file: File }[]>([]);
  const [uploadingStates, setUploadingStates] = useState<{ [key: string]: boolean }>({});
  const [uploadProgress, setUploadProgress] = useState<{ [key: string]: number }>({});
  const lastValidImagesRef = useRef<string[]>([]);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Load messages when component mounts
  useEffect(() => {
    if (deliveryId) {
      fetchMessages();
    }
  }, [deliveryId]);

  // Set up polling for new messages
  useEffect(() => {
    if (!deliveryId) return;
    
    const intervalId = setInterval(() => {
      fetchMessages(false);
    }, 15000); // Poll every 15 seconds
    
    return () => clearInterval(intervalId);
  }, [deliveryId]);

  // Scroll to bottom when messages change
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const fetchMessages = async (showLoading = true) => {
    if (showLoading) setLoading(true);
    try {
      const fetchedMessages = await driverMessageService.getMessages(deliveryId);
      setMessages(fetchedMessages);
    } catch (error) {
      console.error('Error fetching messages:', error);
      if (showLoading) {
        toast.error('Failed to load messages');
      }
    } finally {
      if (showLoading) setLoading(false);
    }
  };

  const handleFileSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (!files || files.length === 0) return;
    
    const file = files[0]; // Get the first file
    
    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast.error(`${file.name} is not an image file`);
      return;
    }
    
    // Create a preview URL
    const localPreviewUrl = imageUploadService.getLocalImageUrl(file);
    console.log('Generated local preview URL:', localPreviewUrl);
    
    // Add to preview images
    const newPreviewImage = { url: localPreviewUrl, file };
    setPreviewImages([...previewImages, newPreviewImage]);
    
    // Set uploading state for this image
    setUploadingStates(prev => ({
      ...prev,
      [localPreviewUrl]: true
    }));
    
    try {
      console.log('Starting upload to R2');
      const uploadedImageUrl = await imageUploadService.uploadImage(file, (progress) => {
        console.log('Upload progress:', progress + '%');
        setUploadProgress(prev => ({
          ...prev,
          [localPreviewUrl]: progress
        }));
      });
      
      console.log('Upload successful, received URL:', uploadedImageUrl);
      
      // Replace the preview image with the uploaded URL
      setPreviewImages(prev => 
        prev.map(img => 
          img.url === localPreviewUrl 
            ? { ...img, url: uploadedImageUrl } 
            : img
        )
      );
      
      // Update images array
      setImages(prev => [...prev, uploadedImageUrl]);
      lastValidImagesRef.current = [...lastValidImagesRef.current, uploadedImageUrl];
      
      // Clean up the blob URL
      imageUploadService.revokeLocalImageUrl(localPreviewUrl);
      
      // Clear uploading state and progress
      setUploadingStates(prev => {
        const newState = { ...prev };
        delete newState[localPreviewUrl];
        return newState;
      });
      
      setUploadProgress(prev => {
        const newState = { ...prev };
        delete newState[localPreviewUrl];
        return newState;
      });
      
      toast.success('Image uploaded successfully');
    } catch (error) {
      console.error('Upload failed:', error);
      
      // Remove failed upload from preview
      setPreviewImages(prev => prev.filter(img => img.url !== localPreviewUrl));
      
      // Try to clean up the blob URL
      imageUploadService.revokeLocalImageUrl(localPreviewUrl);
      
      // Clear uploading state and progress
      setUploadingStates(prev => {
        const newState = { ...prev };
        delete newState[localPreviewUrl];
        return newState;
      });
      
      setUploadProgress(prev => {
        const newState = { ...prev };
        delete newState[localPreviewUrl];
        return newState;
      });
      
      toast.error('Failed to upload image');
    }
    
    // Reset file input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const removeImage = (index: number) => {
    const imageToRemove = previewImages[index];
    
    // Remove from preview images
    const newPreviewImages = [...previewImages];
    newPreviewImages.splice(index, 1);
    setPreviewImages(newPreviewImages);
    
    // If it's a blob URL, revoke it
    if (imageToRemove.url.startsWith('blob:')) {
      URL.revokeObjectURL(imageToRemove.url);
    } else {
      // If it's an uploaded image, remove from images array
      setImages(prev => prev.filter(img => img !== imageToRemove.url));
      lastValidImagesRef.current = lastValidImagesRef.current.filter(img => img !== imageToRemove.url);
    }
  };

  // Handle image change from the ImageUploader (won't be directly used with original UI)
  const handleImagesChange = (newImages: string[]) => {
    console.log('Received new images from ImageUploader:', newImages);
    setImages(newImages);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if ((!messageText || messageText.trim() === '') && previewImages.length === 0) {
      return;
    }
    
    if (!driverId) {
      toast.error('No driver assigned to this delivery');
      return;
    }
    
    setSending(true);
    
    try {
      // Get all non-blob URLs from previewImages (these are already uploaded)
      const uploadedImageUrls = previewImages
        .filter(img => !img.url.startsWith('blob:'))
        .map(img => img.url);
      
      // Now send the message with the uploaded image URLs
      await driverMessageService.sendMessage(deliveryId, {
        content: messageText.trim(),
        images: uploadedImageUrls
      });
      
      // Clear form
      setMessageText('');
      setPreviewImages([]);
      setImages([]);
      
      // Fetch latest messages
      await fetchMessages(false);
    } catch (error) {
      console.error('Error sending message:', error);
      toast.error('Failed to send message');
    } finally {
      setSending(false);
    }
  };

  const formatTime = (timestamp: string) => {
    return format(new Date(timestamp), 'h:mm a');
  };

  if (!deliveryId) {
    return <div className="text-center p-4">Loading...</div>;
  }

  return (
    <div className="flex flex-col h-full bg-gray-50 rounded-lg overflow-hidden">
      {/* Chat header */}
      <div className="bg-amber-500 text-white px-4 py-3 flex items-center">
        <div className="w-10 h-10 rounded-full bg-white text-amber-500 flex items-center justify-center font-bold mr-3">
          {driverName ? driverName.charAt(0).toUpperCase() : 'D'}
        </div>
        <div>
          <h3 className="font-medium">
            {driverId 
              ? `Chat with ${driverName}` 
              : 'No driver assigned'}
          </h3>
          <p className="text-xs text-amber-100">
            {driverId 
              ? 'Send messages and images to your delivery driver' 
              : 'A driver will be assigned to your delivery soon'}
          </p>
        </div>
      </div>
      
      {/* Messages area */}
      <div className="flex-1 overflow-y-auto p-4 space-y-3">
        {loading && messages.length === 0 ? (
          <div className="text-center text-gray-500">Loading messages...</div>
        ) : messages.length === 0 ? (
          <div className="text-center text-gray-500">
            No messages yet. Send a message to start the conversation.
          </div>
        ) : (
          messages.map((message) => (
            <div 
              key={message.id} 
              className={`flex ${message.is_from_driver ? 'justify-start' : 'justify-end'}`}
            >
              <div 
                className={`max-w-[80%] rounded-lg p-3 ${
                  message.is_from_driver 
                    ? 'bg-white text-gray-800 rounded-tl-none' 
                    : 'bg-amber-500 text-white rounded-tr-none'
                }`}
              >
                {message.content && (
                  <p className="whitespace-pre-wrap break-words">{message.content}</p>
                )}
                
                {message.images && message.images.length > 0 && (
                  <div className="mt-2 grid grid-cols-2 gap-1">
                    {message.images.map((image, index) => (
                      <div 
                        key={index}
                        className="relative rounded overflow-hidden"
                      >
                        <img 
                          src={image} 
                          alt={`Image ${index + 1}`}
                          className="w-full h-auto max-h-32 object-cover"
                        />
                      </div>
                    ))}
                  </div>
                )}
                
                <div className={`text-xs mt-1 ${
                  message.is_from_driver ? 'text-gray-500' : 'text-amber-200'
                }`}>
                  {formatTime(message.created_at)}
                </div>
              </div>
            </div>
          ))
        )}
        <div ref={messagesEndRef} />
      </div>
      
      {/* Message input */}
      <form onSubmit={handleSubmit} className="border-t border-gray-200 p-3">
        {/* Image previews */}
        {previewImages.length > 0 && (
          <div className="flex flex-wrap gap-2 mb-2">
            {previewImages.map((image, index) => (
              <div 
                key={index} 
                className="relative w-16 h-16 rounded overflow-hidden bg-gray-100"
              >
                <img 
                  src={image.url} 
                  alt={`Preview ${index + 1}`}
                  className="w-full h-full object-cover"
                />
                {uploadingStates[image.url] ? (
                  <div className="absolute inset-0 bg-black bg-opacity-50 flex flex-col items-center justify-center">
                    <div className="w-6 h-6 border-2 border-white border-t-transparent rounded-full animate-spin mb-1"></div>
                    <span className="text-white text-xs">
                      {uploadProgress[image.url] ? `${uploadProgress[image.url]}%` : 'Uploading...'}
                    </span>
                  </div>
                ) : (
                  <button
                    type="button"
                    onClick={() => removeImage(index)}
                    className="absolute top-0 right-0 bg-red-500 text-white rounded-full p-0.5"
                  >
                    <IoClose size={16} />
                  </button>
                )}
              </div>
            ))}
          </div>
        )}
        
        <div className="flex items-center">
          <button
            type="button"
            onClick={() => fileInputRef.current?.click()}
            className="flex-shrink-0 text-gray-500 hover:text-amber-500 p-2"
            disabled={sending || !driverId}
          >
            <IoImage size={24} />
          </button>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            onChange={handleFileSelect}
            className="hidden"
            disabled={sending || !driverId}
          />
          
          <input
            type="text"
            value={messageText}
            onChange={(e) => setMessageText(e.target.value)}
            placeholder={driverId ? "Type a message..." : "No driver assigned yet"}
            className="flex-1 border border-gray-300 rounded-full py-2 px-4 mx-2 focus:outline-none focus:ring-2 focus:ring-amber-500"
            disabled={sending || !driverId}
          />
          
          <button
            type="submit"
            className={`flex-shrink-0 rounded-full p-2 ${
              (messageText.trim() !== '' || previewImages.length > 0) && driverId
                ? 'bg-amber-500 text-white' 
                : 'bg-gray-200 text-gray-400 cursor-not-allowed'
            }`}
            disabled={
              sending || 
              (!messageText || messageText.trim() === '') && previewImages.length === 0 ||
              !driverId
            }
          >
            <IoSend size={18} />
          </button>
        </div>
      </form>
    </div>
  );
};

export default DriverMessageChat; 